/**
 * Formula Canonicalizer
 * 
 * Deterministic ingestion-time normalizer and structural matcher.
 * Maps parsed formula strings to stable canonical formula IDs in the formula bank.
 * If a formula cannot be confidently resolved, it returns an UNRESOLVED status
 * so the question can be routed to the human authoring/review queue.
 * 
 * STRICT CONSTRAINT: Zero embeddings, zero NLP, zero fuzzy/probabilistic guessing.
 */

export interface CanonicalizationResult {
  formulaId: string | null;
  confidence: 'EXACT' | 'UNRESOLVED';
  reason?: string;
  normalizedString?: string;
}

// Map of canonical normalized structural signatures to formula IDs
const CANONICAL_SIGNATURES: Record<string, string> = {
  // Torricelli
  'v^2=v0^2+2adeltas': 'torricelli',
  'v^2=v0^2+2gdeltas': 'torricelli',
  'v^2=v0^2+2gh': 'torricelli',
  'v^2=2adeltas': 'torricelli',
  'v^2=2gh': 'torricelli',
  'v0^2+2adeltas=v^2': 'torricelli',

  // Segunda Lei de Newton
  'f=ma': 'segunda_lei_newton',
  'fr=ma': 'segunda_lei_newton',
  'f_r=ma': 'segunda_lei_newton',
  'ma=f': 'segunda_lei_newton',
  'a=f/m': 'segunda_lei_newton',
  'm=f/a': 'segunda_lei_newton',

  // Força Peso
  'p=mg': 'forca_peso',
  'mg=p': 'forca_peso',

  // Força de Atrito
  'fat=mun': 'forca_atrito',
  'mun=fat': 'forca_atrito',

  // Quantidade de Movimento & Impulso
  'q=mv': 'quantidade_movimento',
  'mv=q': 'quantidade_movimento',
  'deltaq=mdeltav': 'delta_q',
  'i=fdeltat': 'teorema_impulso',
  'i=deltaq': 'impulso_delta_q',

  // Energia Cinética & Potencial
  'ec=1/2mv^2': 'energia_cinetica',
  'ec=0.5mv^2': 'energia_cinetica',
  'ep=mgdeltas': 'energia_potencial',
  'ep=mgh': 'energia_potencial',
  'em=ec+ep': 'energia_mecanica',

  // Trabalho & Potência
  'tau=fdeltas': 'trabalho_forca',
  'w=fdeltas': 'trabalho_forca',
  'pot=tau/deltat': 'potencia_mecanica',
  'pot=fv': 'potencia_fv',

  // Calorimetria
  'q=mcdeltat': 'calorimetria_sensivel',
  'q=mcdeltas': 'calorimetria_sensivel',
  'q=cdeltat': 'calor_capacidade',
  'c=mc': 'capacidade_termica',
  'q=ml': 'calorimetria_latente',

  // Eletricidade & Eletrodinâmica
  'u=ri': 'primeira_lei_ohm',
  'ri=u': 'primeira_lei_ohm',
  'i=u/r': 'primeira_lei_ohm',
  'r=u/i': 'primeira_lei_ohm',
  'i=q/deltat': 'corrente_eletrica',
  'q=ideltat': 'corrente_eletrica',
  'pel=ui': 'potencia_eletrica_ui',
  'pel=ri^2': 'potencia_eletrica_ri2',
  'pel=u^2/r': 'potencia_eletrica_u2r',
  'eel=peldeltat': 'energia_eletrica',
  'e=pdeltat': 'energia_eletrica',

  // Cinemática básica
  'deltav=v-v0': 'delta_v',
  'v=v0+deltav': 'delta_v',
  'a=deltav/deltat': 'aceleracao_media',
  'v=v0+adeltat': 'muv_vel',
  'deltas=s-s0': 'delta_s',
  'deltas=vdeltat': 'mu_deslocamento',
  's=s0+vdeltat': 'mu_pos',
  'deltas=v0deltat+1/2a(deltat)^2': 'muv_delta_s',
  's=s0+v0deltat+1/2a(deltat)^2': 'muv_pos',
  'vm=deltas/deltat': 'vel_media',
  'vm=(v0+v)/2': 'vel_media_muv',
  'deltas=((v0+v)/2)deltat': 'muv_delta_s_sem_a',

  // Matemática: Parábola / Função Quadrática
  'xv=-b/(2a)': 'vertice_xv',
  'yv=-delta/(4a)': 'vertice_yv',
  'delta=b^2-4ac': 'discriminante_delta',

  // Matemática: Geometria
  'a=pir^2': 'area_circulo',
  'c=2pir': 'comp_circunferencia',
  'd=deltatheta/360*2pir': 'comp_circunferencia',
  'a^2=b^2+c^2': 'teorema_pitagoras',
  'ahip^2=bcat^2+ccat^2': 'teorema_pitagoras',
  'a=(bh)/2': 'area_triangulo',
  'a=(b*h)/2': 'area_triangulo',
};

/**
 * Strips formatting artifacts, normalizes LaTeX operators, simplifies fractions,
 * and canonicalizes variable names into standard representations.
 */
export function normalizeFormulaString(raw: string): string {
  if (!raw) return '';

  let s = raw.toLowerCase();

  // Remove LaTeX wrappers & commands
  s = s.replace(/\\(text|mathrm|mathbf|mathit)\{[^}]*\}/g, '');
  s = s.replace(/\\(left|right|quad|qquad|iff|implies|displaystyle)/g, '');
  s = s.replace(/\\cdot|\\times|\*/g, '');
  s = s.replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, '($1)/($2)');
  s = s.replace(/[\s~_^{}$]/g, '');

  // Normalize delta symbols
  s = s.replace(/\\delta\s*s|deltas/g, 'deltas');
  s = s.replace(/\\delta\s*t|deltat/g, 'deltat');
  s = s.replace(/\\delta\s*v|deltav/g, 'deltav');
  s = s.replace(/\\delta\s*h|deltah/g, 'deltas');
  s = s.replace(/\\delta/g, 'delta');

  // Normalize initial velocity
  s = s.replace(/v0|vo/g, 'v0');

  // Normalize electrical and mechanical power
  s = s.replace(/pel|p_el/g, 'pel');
  s = s.replace(/pot|p_ot/g, 'pot');
  s = s.replace(/eel|e_el/g, 'eel');

  // Normalize pi
  s = s.replace(/\\pi|pi/g, 'pi');

  // Normalize vertex coordinates
  s = s.replace(/xv|x_v/g, 'xv');
  s = s.replace(/yv|y_v/g, 'yv');

  // Remove outermost wrapping parentheses around simple groups
  s = s.replace(/\(([^()]+)\)/g, '$1');

  return s;
}

/**
 * Ingestion-time canonicalizer:
 * Takes any raw parsed formula string and returns either an exact formulaId or UNRESOLVED.
 */
export function canonicalizeFormula(rawFormula: string): CanonicalizationResult {
  if (!rawFormula || !rawFormula.trim()) {
    return {
      formulaId: null,
      confidence: 'UNRESOLVED',
      reason: 'Fórmula em branco ou ausente no enunciado.',
    };
  }

  // Exact check against recognized patterns
  const normalized = normalizeFormulaString(rawFormula);

  if (CANONICAL_SIGNATURES[normalized]) {
    return {
      formulaId: CANONICAL_SIGNATURES[normalized],
      confidence: 'EXACT',
      normalizedString: normalized,
    };
  }

  // Check sub-signatures or known keyword patterns
  if (normalized.includes('v^2=v0^2') || (normalized.includes('v^2=') && normalized.includes('deltas'))) {
    return { formulaId: 'torricelli', confidence: 'EXACT', normalizedString: normalized };
  }

  if (normalized.includes('yv=') && normalized.includes('4a')) {
    return { formulaId: 'vertice_yv', confidence: 'EXACT', normalizedString: normalized };
  }

  if (normalized.includes('xv=') && normalized.includes('2a')) {
    return { formulaId: 'vertice_xv', confidence: 'EXACT', normalizedString: normalized };
  }

  if (normalized.includes('q=mcdeltat') || (normalized.includes('q=') && normalized.includes('mc') && normalized.includes('delta'))) {
    return { formulaId: 'calorimetria_sensivel', confidence: 'EXACT', normalizedString: normalized };
  }

  if (normalized.includes('epdeltat') || (normalized.includes('e=') && normalized.includes('deltat') && normalized.includes('tarifa'))) {
    return { formulaId: 'energia_eletrica', confidence: 'EXACT', normalizedString: normalized };
  }

  if (normalized.includes('f=ma') || normalized.includes('fr=ma') || normalized.includes('a=f/m')) {
    return { formulaId: 'segunda_lei_newton', confidence: 'EXACT', normalizedString: normalized };
  }

  if (normalized.includes('u=ri') || normalized.includes('r=u/i') || normalized.includes('i=u/r')) {
    return { formulaId: 'primeira_lei_ohm', confidence: 'EXACT', normalizedString: normalized };
  }

  if (normalized.includes('2pir') || normalized.includes('deltatheta/360')) {
    return { formulaId: 'comp_circunferencia', confidence: 'EXACT', normalizedString: normalized };
  }

  // If not resolved to an exact canonical formula, DO NOT force match:
  // Route to the Authoring & Review Queue
  return {
    formulaId: null,
    confidence: 'UNRESOLVED',
    reason: `Fórmula bruta "${rawFormula}" não possui correspondência estrutural inequívoca no prontuário oficial. Encaminhada para fila de revisão.`,
    normalizedString: normalized,
  };
}
